/**
 * @fileoverview Hook de Form Layer para gestión del formulario QR Message con validación completa.
 * Implementa el patrón de **Form Layer** con separación estricta de responsabilidades, enfocado únicamente en el estado del formulario, validaciones y operaciones de campos para mensajes QR.
 * Incluye optimizaciones de performance, manejo robusto de errores y documentación completa de flujos y tipos de datos.
 * @module useQrMessageForm
 * @category Hook - Form Layer
 * @since 3.0.0
 * @version 3.1.0 - Optimización de performance, documentación JSDoc completa, separación de capas y manejo avanzado de errores
 */

/**
 * @typedef {Object} QrMessageFormMethods
 * @description Métodos y estado del formulario gestionados por react-hook-form.
 * @property {function} handleSubmit - Envía el formulario y ejecuta validación.
 * @property {function} reset - Reinicia el formulario a valores iniciales.
 * @property {Object} formState - Estado del formulario (errores, isDirty, isValid, etc).
 */

/**
 * @typedef {Object} QrMessageFormReturn
 * @description Objeto retornado por el hook useQrMessageForm.
 * @property {QrMessageFormMethods} methods - Métodos y estado del formulario.
 * @property {Object} errors - Errores de validación actuales.
 * @property {boolean} isValid - Indica si el formulario es válido.
 * @property {boolean} isDirty - Indica si el formulario ha sido modificado.
 * @property {function} resetForm - Reinicia el formulario con valores nuevos o por defecto.
 * @property {function} getMessageType - Determina el tipo de mensaje según archivo adjunto.
 */

import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string, date, array, mixed } from 'yup';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';
import fileUtils from '../../../../util/fileUtils';
import { DEFAULT_FILE_SIZE_TRANSFER } from '../../../../constants/chatFileSizes';

/**
 * Hook de Form Layer para gestión de estado y validación de formularios QR Message.
 *
 * Implementa el patrón de **Form Layer** con separación estricta de responsabilidades:
 * - ✅ Solo gestión de estado y validación de formulario QR.
 * - ✅ Performance optimizada con useMemo, useCallback y react-hook-form.
 * - ✅ No mezcla lógica de UI, acciones ni data layer.
 *
 * **Flujo de operación:**
 * 1. Construye esquema de validación Yup dinámico según tipo y campos.
 * 2. Inicializa react-hook-form con validación y valores iniciales.
 * 3. Provee métodos para reiniciar y obtener tipo de mensaje.
 *
 * @function useQrMessageForm
 * @param {Object} params - Parámetros de inicialización del hook.
 * @param {string} [params.type='message'] - Tipo de operación ('reminder' o 'message').
 * @param {Object} [params.defaultValues={}] - Valores por defecto del formulario.
 * @returns {QrMessageFormReturn} Métodos y estado del formulario QR Message.
 * @see useForm (react-hook-form) Para gestión de estado y validación
 * @see yupResolver Para integración Yup
 * @example
 * const { methods, errors, resetForm } = useQrMessageForm({ ... });
 * @since 3.0.0
 * @version 3.1.0 - Optimización y documentación avanzada
 */
export default function useQrMessageForm({ type = 'message', defaultValues = {} }) {
  const { t } = useTranslation('NewChat');

  /**
   * Esquema Yup de validación dinámica para el formulario QR Message.
   *
   * @type {Object}
   * @memberof useQrMessageForm
   * @description
   * **Flujo:**
   * 1. Valida texto requerido si no hay archivo adjunto.
   * 2. Valida tipo y tamaño de archivo según reglas QR.
   * 3. Si es recordatorio, valida fecha futura.
   */
  const validationSchema = useMemo(() => {
    const baseSchema = {
      templateText: string().test(
        'required-if-no-file',
        t('validation.templateTextRequired') || 'El mensaje es requerido cuando no hay archivo adjunto',
        function (value) {
          const attachedFile = this.parent.attachedFile;
          const hasFile = attachedFile && Array.isArray(attachedFile) && attachedFile.length > 0;
          const hasText = value && value.trim().length > 0;

          // Si no hay archivo, el texto es requerido
          // Si hay archivo, el texto es opcional
          return hasFile || hasText;
        }
      ),
      attachedFile: array()
        .of(mixed())
        .test('file-validation', t('validation.fileInvalid') || 'Archivo inválido', function (files) {
          if (!files || files.length === 0) return true; // Sin archivo es válido

          const file = files[0];
          if (!file) return true;

          // Validaciones basadas en tipo de archivo
          const fileType = file.type;

          // Validar tipos permitidos para QR
          const allowedTypes = [
            'image/jpeg', 'image/jpg', 'image/png',
            'application/pdf',
            'video/mp4', 'video/3gpp'
          ];

          if (!allowedTypes.includes(fileType)) {
            return this.createError({
              message: t('validation.fileTypeNotAllowed') || 'Tipo de archivo no permitido'
            });
          }

          // Validar tamaño según tipo
          if (fileType.startsWith('image/')) {
            if (!fileUtils.isValidImageSizeForWs(file)) {
              return this.createError({
                message: t('validation.imageTooLarge') || 'La imagen es demasiado grande para WhatsApp'
              });
            }
          } else if (fileType === 'application/pdf') {
            if (!fileUtils.isMaxFileSize(file, DEFAULT_FILE_SIZE_TRANSFER)) {
              return this.createError({
                message: t('validation.fileTooLarge') || 'El archivo es demasiado grande'
              });
            }
          } else if (fileType.startsWith('video/')) {
            if (!fileUtils.isValidVideoSizeForWs(file)) {
              return this.createError({
                message: t('validation.videoTooLarge') || 'El video es demasiado grande para WhatsApp'
              });
            }
          }

          return true;
        })
    };

    // Agregar validación de fecha solo para recordatorios
    if (type === 'reminder') {
      baseSchema.sendDate = date()
        .required(t('validation.selectDate') || 'Selecciona una fecha')
        .min(new Date(), t('validation.dateInFuture') || 'La fecha debe ser futura');
    }

    return object().shape(baseSchema);
  }, [type, t]);

  /**
   * Valores por defecto del formulario QR Message, memoizados para performance.
   *
   * @type {Object}
   * @memberof useQrMessageForm
   */
  const memoizedDefaultValues = useMemo(() => ({
    templateText: '',
    sendDate: new Date(),
    attachedFile: [],
    ...defaultValues
  }), [defaultValues]);

  /**
   * Inicializa react-hook-form con el esquema de validación y valores por defecto.
   *
   * @type {QrMessageFormMethods}
   * @memberof useQrMessageForm
   */
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: memoizedDefaultValues,
    mode: 'onChange' // Validación en tiempo real
  });

  const {
    handleSubmit,
    reset,
    formState: { errors, isValid, isDirty }
  } = methods;

  /**
   * Determina el tipo de mensaje según el archivo adjunto.
   *
   * @function getMessageType
   * @memberof useQrMessageForm
   * @param {Array<File>} attachedFile - Array de archivos adjuntos.
   * @returns {string} Tipo de mensaje ('text', 'image', 'document', 'video').
   * @example
   * const type = getMessageType([file]);
   */
  const getMessageType = useCallback((attachedFile) => {
    if (!attachedFile || !Array.isArray(attachedFile) || attachedFile.length === 0) {
      return 'text';
    }

    const file = attachedFile[0];
    if (!file || !file.type) {
      return 'text';
    }

    const fileType = file.type;

    if (fileType.startsWith('image/')) {
      return 'image';
    } else if (fileType === 'application/pdf') {
      return 'document';
    } else if (fileType.startsWith('video/')) {
      return 'video';
    }

    return 'text';
  }, []);

  /**
   * Reinicia el formulario con valores nuevos o por defecto.
   *
   * @function resetForm
   * @memberof useQrMessageForm
   * @param {Object} [newValues] - Valores nuevos para reiniciar el formulario.
   * @example
   * resetForm({ templateText: 'Hola' });
   */
  const resetForm = useCallback((newValues) => {
    reset(newValues || memoizedDefaultValues);
  }, [reset, memoizedDefaultValues]);

  /**
   * Retorna los métodos y estado del formulario QR Message.
   *
   * @returns {QrMessageFormReturn} Métodos y estado del formulario QR Message.
   */
  const returnValue = useMemo(() => ({
    methods,
    handleSubmit,
    errors,
    isValid,
    isDirty,
    resetForm,
    getMessageType
  }), [
    methods,
    handleSubmit,
    errors,
    isValid,
    isDirty,
    resetForm,
    getMessageType
  ]);

  return returnValue;
}